/**
 * Per-symbol AI mode OVERRIDE picker — labeled dropdown.
 *
 *   Inherit         -> follow parent account_pair.aiMode (the default)
 *   Manual          -> pin this symbol regardless of parent
 *   Semi            -> pin semi_auto regardless of parent
 *   Auto            -> pin full_auto regardless of parent
 *
 * Picking Inherit clears the pin (sends `null` to the API). Picking
 * any other value pins that mode. The trigger paints pink whenever a
 * pin is active so the operator sees at a glance that this symbol has
 * drifted off the parent intent.
 *
 * Used to be a 4-state segmented toggle. Replaced with a labeled
 * dropdown so the operator can read what each option does without
 * hovering for tooltips.
 *
 * Disabled while the pair is in 'setup' or 'closed'.
 */

import { useState, type ReactNode } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { PairMode } from "@pfh/shared";

type Slot = "inherit" | PairMode;

interface PairOverrideToggleProps {
  /** Current pin. NULL = inherit. */
  override: PairMode | null;
  /** Parent account_pair's aiMode — surfaced inside the Inherit option
   *  so the operator can see what they're inheriting. */
  parentMode: PairMode;
  status: "setup" | "active" | "closed";
  onChange: (next: PairMode | null) => Promise<void> | void;
  size?: "sm" | "default";
  className?: string;
}

const PARENT_LABEL: Record<PairMode, string> = {
  manual: "Manual",
  semi_auto: "Semi",
  full_auto: "Auto",
};

const PIN_LABEL: Record<PairMode, string> = {
  manual: "Manual",
  semi_auto: "Semi",
  full_auto: "Auto",
};

export function PairOverrideToggle({
  override,
  parentMode,
  status,
  onChange,
  size = "default",
  className,
}: PairOverrideToggleProps): ReactNode {
  const [optimistic, setOptimistic] = useState<{ value: PairMode | null } | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const effective: Slot = (optimistic ? optimistic.value : override) ?? "inherit";
  const disabled = status !== "active" || busy;
  const pinned = effective !== "inherit";

  const handle = async (next: Slot): Promise<void> => {
    if (disabled || next === effective) return;
    const nextValue: PairMode | null = next === "inherit" ? null : next;
    setOptimistic({ value: nextValue });
    setBusy(true);
    try {
      await onChange(nextValue);
    } catch (e) {
      setOptimistic(null);
      console.error("[PairOverrideToggle] override change failed", e);
    } finally {
      setBusy(false);
      queueMicrotask(() => setOptimistic(null));
    }
  };

  const display =
    effective === "inherit"
      ? `Inherit · ${PARENT_LABEL[parentMode]}`
      : PIN_LABEL[effective];

  return (
    <Select
      value={effective}
      onValueChange={(v) => void handle(v as Slot)}
      disabled={disabled}
    >
      <SelectTrigger
        size={size === "sm" ? "sm" : "default"}
        aria-label="Symbol AI mode override"
        className={cn(
          "gap-2",
          size === "sm" ? "text-[11px]" : "text-xs",
          pinned
            ? "border-accent/50 text-accent hover:border-accent/70"
            : "border-input hover:border-accent/40",
          disabled && "opacity-60",
          className,
        )}
      >
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          Override
        </span>
        <SelectValue>
          <span
            className={cn("font-medium", pinned ? "text-accent" : "text-foreground")}
          >
            {display}
          </span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent
        align="end"
        alignItemWithTrigger={false}
        className="min-w-[min(18rem,calc(100vw-1.5rem))]"
      >
        <SelectItem value="inherit">
          <div className="flex flex-col gap-0.5">
            <span className="text-xs font-medium text-foreground">Inherit</span>
            <span className="text-[10px] text-muted-foreground">
              Follow parent ({PARENT_LABEL[parentMode]}).
            </span>
          </div>
        </SelectItem>
        <SelectItem value="manual">
          <div className="flex flex-col gap-0.5">
            <span className="text-xs font-medium text-foreground">Manual</span>
            <span className="text-[10px] text-muted-foreground">
              Pin manual regardless of parent.
            </span>
          </div>
        </SelectItem>
        <SelectItem value="semi_auto">
          <div className="flex flex-col gap-0.5">
            <span className="text-xs font-medium text-foreground">Semi</span>
            <span className="text-[10px] text-muted-foreground">
              Pin semi-auto regardless of parent.
            </span>
          </div>
        </SelectItem>
        <SelectItem value="full_auto">
          <div className="flex flex-col gap-0.5">
            <span className="text-xs font-medium text-foreground">Auto</span>
            <span className="text-[10px] text-muted-foreground">
              Pin full-auto regardless of parent.
            </span>
          </div>
        </SelectItem>
      </SelectContent>
    </Select>
  );
}
